import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'bg-pmm-blue text-primary-foreground hover:bg-pmm-blue/90 cursor-pointer ',
                destructive:
                    'bg-[#FF5858] text-destructive-foreground hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 cursor-pointer ',
                alert: 'bg-[#FF9922] hover:bg-yellow-600 text-alert-foreground text-white hover:text-alert-foreground dark:bg-yellow-500 dark:hover:bg-yellow-600 cursor-pointer ',
                outline:
                    'bg-[#CBDCF0]/60 text-pmm-blue rounded-90 hover:bg-[#CBDCF0]/30 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer ',
                edit: 'bg-yellow-500 text-primary-foreground hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 cursor-pointer ',    
                ghost: 'hover:bg-accent hover:text-accent-foreground cursor-pointer ',
                link: 'text-primary underline-offset-4 hover:underline cursor-pointer ',
                success:
                    'bg-[#138F50] text-primary-foreground hover:bg-[#138F50]/90 dark:bg-green-600 dark:hover:bg-green-700 cursor-pointer ',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button'
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
